import { Box, Container } from "@mui/material"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import * as yup from "yup"

import { Button, Image, TextField, Typography } from "src/UILibrary"

import { Dayjs } from "dayjs"
import { useRecoilState } from "recoil"
import CheckBoxGroupField from "src/components/atom/CheckBoxGroupField"
import InputDateTimeField from "src/components/atom/InputDateTimeField"
import { useLoginContext } from "src/hooks/useLoginContext"
import { ADVISE_TYPE, RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import { validateFields } from "src/modules/validation"
import { Adviser, ConsultService, ProfileService } from "src/openapi"
import { ShowToast } from "src/states/showToast"
import "src/styles/common.scss"
import { ReservationCompleteModal } from "./ReservationCompleteModal"
import "./adviser_reservation.scss"
import { useMediaQuery } from "react-responsive"
import { ShowProgress } from "src/states/showProgress"

export const AdviserReservationPage = () => {
  const { t } = useTranslation("translation", { keyPrefix: "adviser" })

  const isPC = useMediaQuery({ query: "(min-width: 480px)" })

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const { accessToken, startupStages, startupPlans, checkLoginStatus, getJobTitles } =
    useLoginContext()

  const navigate = useNavigate()

  const { state } = useLocation()
  const { adviserID } = state || ""

  if (!adviserID) {
    navigate("/adviser")
  }

  const [hopeTime1, setHopeTime1] = useState<Dayjs | null>(null)
  const [hopeTime2, setHopeTime2] = useState<Dayjs | null>(null)
  const [hopeTime3, setHopeTime3] = useState<Dayjs | null>(null)
  const [adviseType, setAdviseType] = useState<string>()
  const [startStageID, setStartStageID] = useState<number>()
  const [startPlanID, setStartPlanID] = useState<number>()
  const [startupDescription, setStartupDescription] = useState<string>("")
  const [consultDescription, setConsultDescription] = useState<string>("")

  const [modalOpen, setModalOpen] = useState<boolean>(false)

  const [adviser, setAdviser] = useState<Adviser>()

  const [adviseTypeValidationMessage, setAdviseTypeValidatMessage] = useState<string>("")
  const [stageValidationMessage, setStageValidationMessage] = useState<string>("")
  const [planValidationMessage, setPlanValidationMessage] = useState<string>("")

  useEffect(() => {
    ProfileService.adviserDetail(adviserID)
      .then((res) => {
        if (res.status === RESPONSE_CODE.SUCCESS) {
          setAdviser(res.data)
        } else {
          setShowToast({ show: true, text: t("failed_get_detail"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowToast({ show: true, text: t("failed_get_detail"), type: SNACKBAR_TYPES.ERROR })
      })
  }, [adviserID])

  const validate = async () => {
    let validation = true

    try {
      await validateFields(
        yup.object().shape({
          hopeTime1: yup.string().required(t("please_select_hope_time")),
          hopeTime2: yup.string().nullable(t("please_select_hope_time")),
          hopeTime3: yup.string().nullable(t("please_select_hope_time")),
        }),
        ["hopeTime1", "hopeTime2", "hopeTime3"],
        [hopeTime1, hopeTime2, hopeTime3]
      )
      validation = validation && true
    } catch (err: any) {
      setShowToast({ show: true, text: err.message, type: SNACKBAR_TYPES.WARNING })
      validation = validation && false
    }

    if (!adviseType) {
      validation = validation && false
      setAdviseTypeValidatMessage(t("please_select_advise_type"))
    }

    if (!startStageID) {
      validation = validation && false
      setStageValidationMessage(t("please_select_stage"))
    }

    if (!startPlanID) {
      validation = validation && false
      setPlanValidationMessage(t("please_select_plan"))
    }

    return validation
  }

  const handleReserve = async () => {
    if (!(await validate())) {
      return
    }

    setShowProgress(true)
    ConsultService.createConsult(accessToken, {
      adviserUserID: adviserID,
      method: adviseType as string,
      hopeDate1: hopeTime1?.format("YYYY-MM-DD HH:mm:ss") as string,
      hopeDate2:
        hopeTime2 && hopeTime2.isValid() ? hopeTime2.format("YYYY-MM-DD HH:mm:ss") : undefined,
      hopeDate3:
        hopeTime3 && hopeTime2?.isValid() ? hopeTime3.format("YYYY-MM-DD HH:mm:ss") : undefined,
      startupStageID: startStageID as number,
      startupPlanID: startPlanID as number,
      startupDescription: startupDescription,
      consultDescription: consultDescription,
    })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.CREATE_SUCCESS) {
          setShowToast({ show: true, text: t("success_reserve"), type: SNACKBAR_TYPES.SUCCESS })
          setModalOpen(true)
        } else {
          setShowToast({ show: true, text: t("failed_reserve"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_reserve"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  useEffect(() => {
    if (startupDescription.length > 400) {
      setShowToast({
        show: true,
        text: t("please_input_less_than_characters", { length: 400 }),
        type: SNACKBAR_TYPES.WARNING,
      })
      setStartupDescription(startupDescription.slice(400))
    }
  }, [startupDescription])

  useEffect(() => {
    if (consultDescription.length > 400) {
      setShowToast({
        show: true,
        text: t("please_input_less_than_characters", { length: 400 }),
        type: SNACKBAR_TYPES.WARNING,
      })
      setConsultDescription(consultDescription.slice(400))
    }
  }, [consultDescription])

  return (
    <>
      <Container maxWidth="lg" className="title main-body-with-footer">
        <Typography.Large color="textPrimary" sx={{ mt: "10px" }}>
          {t("reservation")}
        </Typography.Large>
        <Typography.SubHeadNormal
          sx={{ cursor: "pointer" }}
          className="back-btn"
          onClick={() =>
            navigate("/adviser/detail", {
              state: { adviserID: adviser?.userId, userID: adviser?.id },
            })
          }
        >
          {t("back")}
        </Typography.SubHeadNormal>
        <Container maxWidth="md" className="adviser-abs-container" sx={{ bgcolor: "#FBFAF4" }}>
          <Typography.SubHeadNormal>{t("adviser_detail_description")}</Typography.SubHeadNormal>
          <Box className="adviser-abs-detail" sx={{ mt: "20px" }}>
            <Image
              src={adviser ? (adviser?.photoUrl as string) : ""}
              sx={{ width: "auto", height: "300px", bgcolor: "divider" }}
              imageStyle={{ width: "100%", height: "100%" }}
              alt=""
            />
            <Box sx={{ display: "flex", flexDirection: "column", p: isPC ? "20px" : "10px" }}>
              <Typography.SubHeadNormal>
                {adviser
                  ? `${adviser?.lastName} ${adviser?.firstName}${
                      adviser.isResident ? `(${t("resident_adviser")})` : ""
                    }`
                  : ""}
              </Typography.SubHeadNormal>
              <Typography.SubHeadNormal
                className="job-item "
                sx={{ mt: "20px", fontFamily: "Noto Sans JP !important" }}
              >
                {getJobTitles(adviser)}
              </Typography.SubHeadNormal>
            </Box>
          </Box>
        </Container>
        <Container maxWidth="md" sx={{ mt: "60px", mb: "40px", p: "20px", bgcolor: "#FBFAF4" }}>
          <Box display="flex" sx={{ mb: "8px" }}>
            <Typography.SubHeadNormal sx={{ mr: "8px" }}>
              {t("advise_hope_datetime")}
            </Typography.SubHeadNormal>
            <Box
              sx={{
                px: "8px",
                borderWidth: "1px",
                borderStyle: "solid",
                borderRadius: "4px",
                borderColor: "text.border",
                display: "flex",
              }}
            >
              <Typography.InputFieldRequired sx={{ m: "auto", color: "text.border" }}>
                {t("required")}
              </Typography.InputFieldRequired>
            </Box>
          </Box>
          <InputDateTimeField
            name={t("hope_1")}
            datetime={hopeTime1}
            handleDateTime={setHopeTime1}
            alignHorizontal
          />
          <InputDateTimeField
            name={t("hope_2")}
            datetime={hopeTime2}
            handleDateTime={setHopeTime2}
            alignHorizontal
          />
          <InputDateTimeField
            name={t("hope_3")}
            datetime={hopeTime3}
            handleDateTime={setHopeTime3}
            alignHorizontal
          />
          <CheckBoxGroupField
            name={t("advise_type")}
            datas={[
              {
                label: t("meeting"),
                value: ADVISE_TYPE.MEETING,
              },
              /*
              {
                label: t("call"),
                value: ADVISE_TYPE.CALL,
              },
              */
              {
                label: t("online"),
                value: ADVISE_TYPE.ONLINE,
              },
            ]}
            selected={adviseType}
            handleSelected={setAdviseType}
            radio
            required
            sx={{ mt: "20px" }}
            validationMessage={adviseTypeValidationMessage}
          />
          <CheckBoxGroupField
            name={t("start_step")}
            radio
            required
            sx={{ mt: "20px" }}
            datas={startupStages}
            selected={startStageID}
            handleSelected={setStartStageID}
            validationMessage={stageValidationMessage}
          />
          <CheckBoxGroupField
            name={t("start_plan")}
            radio
            required
            sx={{ mt: "20px" }}
            datas={startupPlans}
            selected={startPlanID}
            handleSelected={setStartPlanID}
            validationMessage={planValidationMessage}
          />
          <Typography.SubHeadNormal sx={{ mt: "20px" }}>
            {t("business_overview")}
          </Typography.SubHeadNormal>
          <TextField
            multiline
            variant="standard"
            rows={5}
            fullWidth
            placeholder={t("business_overview_placeholder")}
            sx={{
              bgcolor: "transparent",
            }}
            value={startupDescription}
            onChange={(event) => setStartupDescription(event.target.value)}
          />

          <Typography.SubHeadNormal sx={{ mt: "20px" }}>
            {t("consultant_summary")}
          </Typography.SubHeadNormal>
          <TextField
            multiline
            variant="standard"
            rows={5}
            fullWidth
            placeholder={t("consultant_summary_placeholder")}
            sx={{
              bgcolor: "transparent",
            }}
            value={consultDescription}
            onChange={(event) => setConsultDescription(event.target.value)}
          />
          <Button
            variant="contained"
            sx={{
              minWidth: "160px",
              mt: "40px",
              py: "10px",
              fontSize: "1rem",
              fontFamily: "Noto Sans JP !important",
              lineHeight: "1rem",
              borderRadius: "0px",
              ":hover": {
                bgcolor: "#2DA389",
              },
            }}
            onClick={() => {
              handleReserve()
            }}
          >
            {t("send")}
          </Button>
        </Container>
      </Container>
      <ReservationCompleteModal open={modalOpen} handleOpen={setModalOpen} item={adviser} />
    </>
  )
}
